import { useState, useCallback, useEffect, useMemo } from "react";
import { useWatch } from "react-hook-form";
import {
  BulkDeleteButton,
  Create,
  Datagrid,
  DeleteButton,
  Edit,
  EditButton,
  List,
  Pagination,
  SaveButton,
  SimpleForm,
  TextField,
  TextInput,
  SelectInput,
  Toolbar,
  TopToolbar,
  CreateButton,
  WrapperField,
  useDataProvider,
  useNotify,
  useRefresh,
  useRecordContext,
  useInput,
} from "react-admin";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Link,
  Box,
  Chip,
  IconButton,
  TextField as MuiTextField,
  Avatar,
  Stack,
  Tooltip,
  Divider,
  Autocomplete,
  Card,
  CardMedia,
  CardContent,
  Grid,
  Pagination as MuiPagination,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import MuiForm from "@rjsf/mui";
import type { RJSFSchema, UiSchema } from "@rjsf/utils";
import validator from "@rjsf/validator-ajv8";

const UrlField = ({ source, label }: { source: string; label?: string }) => {
  const record = useRecordContext();
  const value = record?.[source];
  if (!value) return null;
  return (
    <Link href={value} target="_blank" rel="noopener noreferrer" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
      {value}
    </Link>
  );
};

const isImageUrl = (val: string) => /^https?:\/\/.+\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(val);

const JsonField = ({ source, label }: { source: string; label?: string }) => {
  const record = useRecordContext();
  const value = record?.[source];
  if (value == null) return null;

  if (Array.isArray(value)) {
    return (
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {value.map((v, i) => (
          <Chip key={i} label={String(v)} size="small" variant="outlined" />
        ))}
      </Stack>
    );
  }

  if (typeof value === "object") {
    const entries = Object.entries(value);
    return (
      <Stack spacing={1} sx={{ maxWidth: 420 }}>
        {entries.map(([key, val]) => (
          <Box key={key}>
            <Chip label={key} size="small" color="primary" sx={{ fontWeight: 600, mb: 0.5 }} />
            {Array.isArray(val) ? (
              <Stack spacing={0.5} sx={{ ml: 0.5 }}>
                {val.map((item: any, idx: number) => {
                  if (typeof item === "object" && item !== null) {
                    const imageEntry = Object.entries(item).find(
                      ([, v]) => typeof v === "string" && isImageUrl(v as string)
                    );
                    return (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 0.75,
                          bgcolor: "action.hover",
                          borderRadius: 1,
                        }}
                      >
                        {imageEntry && (
                          <Tooltip title={imageEntry[1] as string}>
                            <Avatar src={imageEntry[1] as string} variant="rounded" sx={{ width: 32, height: 32 }} />
                          </Tooltip>
                        )}
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          {Object.entries(item).map(([k, v]) =>
                            typeof v === "string" && isImageUrl(v) ? null : (
                              <Tooltip key={k} title={`${k}: ${String(v)}`}>
                                <Chip
                                  label={`${k}: ${String(v)}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ maxWidth: 200, fontSize: "0.75rem" }}
                                />
                              </Tooltip>
                            )
                          )}
                        </Stack>
                      </Box>
                    );
                  }
                  return <Chip key={idx} label={String(item)} size="small" variant="outlined" />;
                })}
              </Stack>
            ) : (
              <Typography variant="body2" sx={{ ml: 0.5 }}>
                {String(val)}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    );
  }

  return <span>{String(value)}</span>;
};
// Related PDP Cross Sell field — show pageTagId with country annotation
let PDP_GROUP_MAP: Record<string, string> | null = null;
const RelatedPdpCrossSellField = ({ label }: { label?: string }) => {
  const record = useRecordContext();
  const value = record?.relatedPdpCrossSell;
  const dataProvider = useDataProvider();
  const [map, setMap] = useState<Record<string, string> | null>(PDP_GROUP_MAP);
  const [loading, setLoading] = useState(!PDP_GROUP_MAP);

  useEffect(() => {
    let cancelled = false;
    if (PDP_GROUP_MAP) {
      setMap(PDP_GROUP_MAP);
      setLoading(false);
      return;
    }
    const fetch = async () => {
      setLoading(true);
      try {
        const { data } = await dataProvider.getList("pdpGroup", {
          sort: { field: "pageTagId", order: "ASC" },
          pagination: { page: 1, perPage: 10000 },
          filter: {},
        });
        if (cancelled) return;
        const m: Record<string, string> = {};
        data.forEach((d: any) => {
          if (d.pageTagId) m[d.pageTagId] = d.country || "";
        });
        PDP_GROUP_MAP = m;
        setMap(m);
      } catch (e) {
        console.error("Failed to load PDP groups", e);
        if (!cancelled) setMap({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => {
      cancelled = true;
    };
  }, [dataProvider]);

  if (value == null) return null;
  const parsed =
    typeof value === "string"
      ? (() => {
          try {
            return JSON.parse(value);
          } catch {
            return [];
          }
        })()
      : value;
  const items: string[] = Array.isArray(parsed) ? parsed : [];
  if (items.length === 0) return null;

  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
      {items.map((p, i) => {
        const c = map?.[p];
        return (
          <Chip
            key={i}
            label={`${p}${c ? ` (${c})` : ""}`}
            size="small"
            variant="outlined"
            title={c ? `${p} (${c})` : String(p)}
          />
        );
      })}
    </Stack>
  );
};

// Compact display for datagrid — shows small avatars with tooltips
const BundleListCompactField = ({ source, label }: { source: string; label?: string }) => {
  const record = useRecordContext();
  const value = record?.[source];
  if (!Array.isArray(value) || value.length === 0) return null;

  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ maxWidth: 200 }}>
      {value.slice(0, 5).map((item: any, idx: number) => (
        <Tooltip key={idx} title={item.bundle_name || item.name || item.bundle_id || ""}>
          <Avatar src={item.bundle_image || item.image} variant="rounded" sx={{ width: 32, height: 32 }} />
        </Tooltip>
      ))}
      {value.length > 5 && <Chip label={`+${value.length - 5}`} size="small" variant="outlined" />}
    </Stack>
  );
};

// Full display for edit page — shows image + name cards in a grid
const BundleListField = ({ source, label }: { source: string; label?: string }) => {
  const record = useRecordContext();
  const value = record?.[source];
  if (!Array.isArray(value) || value.length === 0) return null;

  return (
    <Grid container spacing={2} sx={{ width: "100%" }}>
      {value.map((item: any, idx: number) => (
        <Grid item xs={12} sm={6} md={4} key={idx}>
          <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {(item.bundle_image || item.image) && (
              <CardMedia
                component="img"
                height="160"
                image={item.bundle_image || item.image}
                alt={item.bundle_name || item.name || "Bundle item"}
                sx={{ objectFit: "contain", bgcolor: "grey.50", p: 1 }}
              />
            )}
            <CardContent sx={{ flexGrow: 1, p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {item.bundle_name || item.name || "Unnamed bundle"}
              </Typography>
              <Chip label={item.bundle_id} size="small" variant="outlined" sx={{ fontSize: "0.75rem" }} />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

const PdpGroupFilters = [
  <TextInput label="Page Tag Id" source="page_tag_id__icontains" alwaysOn />,
  <TextInput label="Country" source="country__icontains" alwaysOn />,
  <TextInput label="URL" source="url__icontains" alwaysOn />,
];

const MyPagination = () => <Pagination rowsPerPageOptions={[10, 25, 50, 100, 500, 1000]} />;

const parseCsv = (text: string) => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) return { headers: [], rows: [] };

  const headers = lines[0].split(",").map((h) => h.trim());
  const rows = lines.slice(1).map((line) => {
    const values = line.split(",").map((v) => v.trim());
    return headers.reduce<Record<string, string>>((obj, header, i) => {
      obj[header] = values[i] || "";
      return obj;
    }, {});
  });

  return { headers, rows };
};

const JSON_FIELDS = ["relatedPdpCrossSell", "filterList", "bundleList"];

const parseJsonFields = (row: Record<string, string>) => {
  const parsed: Record<string, any> = { ...row };
  for (const field of JSON_FIELDS) {
    if (parsed[field] && typeof parsed[field] === "string") {
      try {
        parsed[field] = JSON.parse(parsed[field]);
      } catch {
        // leave as-is if not valid JSON
      }
    }
  }
  return parsed;
};

const CsvImportButton = () => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<{ headers: string[]; rows: Record<string, string>[] } | null>(null);
  const [importing, setImporting] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const result = parseCsv(text);
      if (result.rows.length === 0) {
        notify("CSV file is empty or has no data rows", { type: "warning" });
        return;
      }
      setPreview(result);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!preview) return;
    setImporting(true);

    let success = 0;
    let failed = 0;

    for (const row of preview.rows) {
      try {
        await dataProvider.create("pdpGroup", { data: parseJsonFields(row) });
        success++;
      } catch (err) {
        failed++;
      }
    }

    setImporting(false);
    setOpen(false);
    setPreview(null);
    notify(`Import complete: ${success} created, ${failed} failed`, {
      type: failed > 0 ? "warning" : "success",
    });
    refresh();
  };

  const handleClose = () => {
    setOpen(false);
    setPreview(null);
  };

  return (
    <>
      <Button startIcon={<UploadFileIcon />} size="small" onClick={() => setOpen(true)}>
        Import CSV
      </Button>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Import PDP Groups from CSV</DialogTitle>
        <DialogContent>
          <input type="file" accept=".csv" onChange={handleFileChange} style={{ marginBottom: 16 }} />
          {preview && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Columns: {preview.headers.join(", ")}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Rows to import: {preview.rows.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Preview (first 5 rows):
              </Typography>
              <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8, fontSize: "0.85em" }}>
                <thead>
                  <tr>
                    {preview.headers.map((h) => (
                      <th key={h} style={{ border: "1px solid #ddd", padding: 4, textAlign: "left" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.slice(0, 5).map((row, i) => (
                    <tr key={i}>
                      {preview.headers.map((h) => (
                        <td key={h} style={{ border: "1px solid #ddd", padding: 4 }}>
                          {row[h]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleImport} disabled={!preview || importing} variant="contained">
            {importing ? <CircularProgress size={20} /> : `Import ${preview?.rows.length ?? 0} rows`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const ListActions = () => (
  <TopToolbar>
    <CsvImportButton />
    <CreateButton />
  </TopToolbar>
);

// LIST
export const PdpGroupList = () => (
  <List
    filters={PdpGroupFilters}
    sort={{ field: "pageTagId", order: "ASC" }}
    pagination={<MyPagination />}
    actions={<ListActions />}
  >
    <Datagrid bulkActionButtons={<BulkDeleteButton mutationMode="pessimistic" confirmTitle="Delete PDP Groups" />}>
      <TextField source="pdpGroupId" label="PDP Group ID" />
      <TextField source="pageTagId" label="Page Tag Id" />
      <UrlField source="url" label="URL" />
      <TextField source="country" label="Country" />
      <RelatedPdpCrossSellField label="Related PDP Cross Sell" />
      <JsonField source="filterList" label="Filter List" />
      <JsonField source="filterValue" label="Filter Value" />
      <BundleListCompactField source="bundleList" label="Bundle List" />
      <WrapperField label="Actions">
        <EditButton />
      </WrapperField>
    </Datagrid>
  </List>
);

// JSON Schema Form Input — replicates <JsonSchemaForm> from react-admin enterprise
import { TranslatableString as TS } from "@rjsf/utils";

const rjsfTranslateString = (stringKey: TS, params?: string[]): string => {
  // Default English translations
  const defaults: Record<string, string> = {
    [TS.AddButton]: "Add",
    [TS.AddItemButton]: "Add Item",
    [TS.CopyButton]: "Copy",
    [TS.MoveDownButton]: "Move down",
    [TS.MoveUpButton]: "Move up",
    [TS.RemoveButton]: "Remove",
    [TS.YesLabel]: "Yes",
    [TS.NoLabel]: "No",
  };
  let str = defaults[stringKey] ?? stringKey;
  if (params) {
    params.forEach((p, i) => {
      str = str.replace(`%${i + 1}`, p);
    });
  }
  return str;
};

const RjsfInput = ({
  source,
  label,
  schema,
  uiSchema,
}: {
  source: string;
  label: string;
  schema: RJSFSchema;
  uiSchema?: UiSchema;
}) => {
  const {
    field: { value, onChange },
  } = useInput({ source });

  const handleChange = useCallback(
    (data: any) => {
      onChange(data.formData);
    },
    [onChange]
  );

  return (
    <Box sx={{ mb: 2, width: "100%" }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
        {label}
      </Typography>
      <MuiForm
        schema={schema}
        uiSchema={{
          "ui:submitButtonOptions": { norender: true },
          "ui:globalOptions": { copyable: false },
          ...uiSchema,
        }}
        formData={value ?? (schema.type === "array" ? [] : {})}
        onChange={handleChange}
        validator={validator}
        liveValidate={false}
        translateString={rjsfTranslateString}
      />
    </Box>
  );
};

// Related PDP Cross Sell — autocomplete populated from existing pdpGroup pdpGroupIds
const RelatedPdpCrossSellInput = ({ source, label }: { source: string; label: string }) => {
  const {
    field: { value, onChange },
  } = useInput({ source });
  const country = useWatch({ name: "country" });
  const dataProvider = useDataProvider();
  const [options, setOptions] = useState<{ pageTagId: string; country: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await dataProvider.getList("pdpGroup", {
          sort: { field: "pageTagId", order: "ASC" },
          pagination: { page: 1, perPage: 10000 },
          filter: {},
        });
        setOptions(data.map((d: any) => ({ pageTagId: d.pageTagId, country: d.country || "" })));
      } catch (e) {
        console.error("Failed to load PDP groups", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dataProvider]);

  const parsed =
    typeof value === "string"
      ? (() => {
          try {
            return JSON.parse(value);
          } catch {
            return [];
          }
        })()
      : value;
  const selected: string[] = Array.isArray(parsed) ? parsed : [];
  const selectedCountry = typeof country === "string" ? country.trim() : "";
  const normalizedCountry = selectedCountry.toLowerCase();

  const filteredOptions = useMemo(() => {
    if (!normalizedCountry) return [];
    return options.filter((o) => (o.country || "").toLowerCase() === normalizedCountry);
  }, [options, normalizedCountry]);

  const selectedOptions = useMemo(
    () =>
      selected.map(
        (id) =>
          filteredOptions.find((o) => o.pageTagId === id) ||
          options.find((o) => o.pageTagId === id && (o.country || "").toLowerCase() === normalizedCountry) ||
          options.find((o) => o.pageTagId === id) || { pageTagId: id, country: "" }
      ),
    [selected, filteredOptions, options, normalizedCountry]
  );

  return (
    <Box sx={{ mb: 2, width: "100%" }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
        {label}
      </Typography>
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Autocomplete
          multiple
          options={filteredOptions}
          value={selectedOptions}
          onChange={(_, newValue) => onChange(newValue.map((v) => v.pageTagId))}
          getOptionLabel={(option) => `${option.pageTagId}${option.country ? ` (${option.country})` : ""}`}
          isOptionEqualToValue={(option, val) => option.pageTagId === val.pageTagId && option.country === val.country}
          disabled={!selectedCountry}
          noOptionsText={
            selectedCountry ? `No PDP groups found for country \"${selectedCountry}\"` : "Select country first"
          }
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label="Select related PDP groups"
              variant="outlined"
              size="small"
              helperText={
                selectedCountry
                  ? `Showing PDP groups for country: ${selectedCountry}`
                  : "Enter country first to choose related PDP groups"
              }
            />
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.pageTagId}
                label={`${option.pageTagId}${option.country ? ` (${option.country})` : ""}`}
                size="small"
              />
            ))
          }
          freeSolo={false}
          filterSelectedOptions
          sx={{ width: "100%" }}
        />
      )}
    </Box>
  );
};

const filterListSchema: RJSFSchema = {
  type: "array",
  title: "",
  items: { type: "string", title: "Filter" },
};

// Filter List Input — autocomplete populated from product tag categories
const FilterListInput = ({ source, label }: { source: string; label: string }) => {
  const {
    field: { value, onChange },
  } = useInput({ source });
  const dataProvider = useDataProvider();
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: cats } = await dataProvider.getMany("productTagCategories", {} as any);
        const sorted = cats.filter((c: any) => c.id !== "").sort((a: any, b: any) => a.name.localeCompare(b.name));
        setCategories(sorted);
      } catch (e) {
        console.error("Failed to load product tag categories", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dataProvider]);

  const parsed2 =
    typeof value === "string"
      ? (() => {
          try {
            return JSON.parse(value);
          } catch {
            return [];
          }
        })()
      : value;
  const selected: string[] = Array.isArray(parsed2) ? parsed2 : [];

  return (
    <Box sx={{ mb: 2, width: "100%" }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
        {label}
      </Typography>
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Autocomplete
          multiple
          options={categories}
          value={categories.filter((c) => selected.includes(c.id))}
          onChange={(_, newValue) => onChange(newValue.map((v) => v.id))}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, val) => option.id === val.id}
          renderInput={(params) => (
            <MuiTextField {...params} label="Select tag categories" variant="outlined" size="small" />
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip {...getTagProps({ index })} key={option.id} label={option.name} size="small" />
            ))
          }
          freeSolo={false}
          filterSelectedOptions
          sx={{ width: "100%" }}
        />
      )}
    </Box>
  );
};

const bundleListSchema: RJSFSchema = {
  type: "object",
  title: "",
  additionalProperties: {
    type: "array",
    items: { type: "string" },
  },
};

const bundleListUiSchema: UiSchema = {
  "ui:options": { orderable: false },
};

// Custom Bundle List Input — each item is an object with arbitrary key-value pairs
const BundleListInput = ({ source, label }: { source: string; label: string }) => {
  const {
    field: { value, onChange },
  } = useInput({ source });

  const data: Record<string, Record<string, string>[]> =
    value && typeof value === "object" && !Array.isArray(value) ? value : {};

  const updateData = (newData: Record<string, Record<string, string>[]>) => {
    onChange({ ...newData });
  };

  const handleAddGroupKey = () => {
    const keyName = prompt("Enter new group key name:");
    if (!keyName || keyName.trim() === "") return;
    updateData({ ...data, [keyName.trim()]: [{}] });
  };

  const handleRenameGroupKey = (oldKey: string, newKey: string) => {
    if (!newKey.trim() || newKey.trim() === oldKey) return;
    const entries = Object.entries(data);
    const newData: Record<string, Record<string, string>[]> = {};
    for (const [k, v] of entries) {
      newData[k === oldKey ? newKey.trim() : k] = v;
    }
    updateData(newData);
  };

  const handleRemoveGroupKey = (key: string) => {
    const next = { ...data };
    delete next[key];
    updateData(next);
  };

  const handleAddItem = (groupKey: string) => {
    // Clone keys from first item as empty template, or blank object
    const existing = data[groupKey];
    const template: Record<string, string> =
      existing && existing.length > 0 ? Object.fromEntries(Object.keys(existing[0]).map((k) => [k, ""])) : {};
    updateData({ ...data, [groupKey]: [...(data[groupKey] || []), template] });
  };

  const handleRemoveItem = (groupKey: string, index: number) => {
    const arr = [...(data[groupKey] || [])];
    arr.splice(index, 1);
    updateData({ ...data, [groupKey]: arr });
  };

  // Change a property value within an item
  const handleChangeValue = (groupKey: string, itemIndex: number, propKey: string, newValue: string) => {
    const arr = [...(data[groupKey] || [])];
    arr[itemIndex] = { ...arr[itemIndex], [propKey]: newValue };
    updateData({ ...data, [groupKey]: arr });
  };

  // Rename a property key within an item
  const handleRenameProperty = (groupKey: string, itemIndex: number, oldPropKey: string, newPropKey: string) => {
    if (!newPropKey.trim() || newPropKey.trim() === oldPropKey) return;
    const arr = [...(data[groupKey] || [])];
    const item = { ...arr[itemIndex] };
    const newItem: Record<string, string> = {};
    for (const [k, v] of Object.entries(item)) {
      newItem[k === oldPropKey ? newPropKey.trim() : k] = v;
    }
    arr[itemIndex] = newItem;
    updateData({ ...data, [groupKey]: arr });
  };

  // Remove a property from an item
  const handleRemoveProperty = (groupKey: string, itemIndex: number, propKey: string) => {
    const arr = [...(data[groupKey] || [])];
    const item = { ...arr[itemIndex] };
    delete item[propKey];
    arr[itemIndex] = item;
    updateData({ ...data, [groupKey]: arr });
  };

  // Add a new property to an item
  const handleAddProperty = (groupKey: string, itemIndex: number) => {
    const propName = prompt("Enter new property name:");
    if (!propName || propName.trim() === "") return;
    const arr = [...(data[groupKey] || [])];
    arr[itemIndex] = { ...arr[itemIndex], [propName.trim()]: "" };
    updateData({ ...data, [groupKey]: arr });
  };

  return (
    <Box sx={{ mb: 2, width: "100%" }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
        {label}
      </Typography>
      {Object.entries(data).map(([groupKey, items]) => (
        <Box key={groupKey} sx={{ mb: 2, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, gap: 1 }}>
            <MuiTextField
              value={groupKey}
              onChange={(e) => handleRenameGroupKey(groupKey, e.target.value)}
              size="small"
              variant="standard"
              fullWidth
              sx={{ fontWeight: 600 }}
              InputProps={{ sx: { fontWeight: 600, fontSize: "1.05rem" } }}
            />
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => handleRemoveGroupKey(groupKey)}
            >
              Remove All
            </Button>
          </Box>
          {(items || []).map((item, itemIdx) => (
            <Box
              key={itemIdx}
              sx={{ ml: 1, mb: 1.5, p: 1.5, border: "1px dashed", borderColor: "grey.400", borderRadius: 1 }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                Item {itemIdx + 1}
              </Typography>
              {Object.entries(item).map(([propKey, propVal]) => (
                <Box key={propKey} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <MuiTextField
                    value={propKey}
                    onChange={(e) => handleRenameProperty(groupKey, itemIdx, propKey, e.target.value)}
                    size="small"
                    variant="outlined"
                    sx={{ width: 160, flexShrink: 0 }}
                    label="Key"
                  />
                  <MuiTextField
                    value={propVal}
                    onChange={(e) => handleChangeValue(groupKey, itemIdx, propKey, e.target.value)}
                    size="small"
                    variant="outlined"
                    fullWidth
                    label="Value"
                  />
                  {typeof propVal === "string" && isImageUrl(propVal) && (
                    <img
                      src={propVal}
                      alt={propKey}
                      style={{ height: 36, width: 36, objectFit: "cover", borderRadius: 4, flexShrink: 0 }}
                    />
                  )}
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveProperty(groupKey, itemIdx, propKey)}
                    title="Remove property"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                <Button size="small" startIcon={<AddIcon />} onClick={() => handleAddProperty(groupKey, itemIdx)}>
                  Add Property
                </Button>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemoveItem(groupKey, itemIdx)}
                  title="Remove item"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={() => handleAddItem(groupKey)}>
            Add Item
          </Button>
        </Box>
      ))}
      <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddGroupKey} sx={{ mt: 1 }}>
        Add Key
      </Button>
    </Box>
  );
};

const BUNDLES_PER_PAGE = 6;

// Watches relatedPdpCrossSell and fetches bundle lists for each selected PDP group
const DynamicBundleList = () => {
  const dataProvider = useDataProvider();
  const relatedPdpCrossSell = useWatch({ name: "relatedPdpCrossSell" });
  const [bundles, setBundles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const selected: string[] = useMemo(() => {
    const val =
      typeof relatedPdpCrossSell === "string"
        ? (() => {
            try {
              return JSON.parse(relatedPdpCrossSell);
            } catch {
              return [];
            }
          })()
        : relatedPdpCrossSell;
    return Array.isArray(val) ? val : [];
  }, [relatedPdpCrossSell]);

  useEffect(() => {
    if (selected.length === 0) {
      setBundles([]);
      return;
    }

    let cancelled = false;
    const fetchBundles = async () => {
      setLoading(true);
      try {
        const { data } = await dataProvider.getList("pdpGroup", {
          sort: { field: "pageTagId", order: "ASC" },
          pagination: { page: 1, perPage: 10000 },
          filter: {},
        });
        if (cancelled) return;
        const matched = data.filter((d: any) => selected.includes(d.pageTagId));
        const allBundles = matched.flatMap((d: any) =>
          Array.isArray(d.bundleList) ? d.bundleList.map((b: any) => ({ ...b, _fromGroup: d.pageTagId })) : []
        );
        setBundles(allBundles);
      } catch (e) {
        console.error("Failed to fetch bundle list", e);
        if (!cancelled) setBundles([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchBundles();
    return () => {
      cancelled = true;
    };
  }, [selected, dataProvider]);

  useEffect(() => {
    setPage(1);
  }, [bundles.length]);

  if (loading) {
    return (
      <Box sx={{ mt: 3, width: "100%", display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading bundles...
        </Typography>
      </Box>
    );
  }
  if (bundles.length === 0) return null;

  const totalPages = Math.ceil(bundles.length / BUNDLES_PER_PAGE);
  const paged = bundles.slice((page - 1) * BUNDLES_PER_PAGE, page * BUNDLES_PER_PAGE);

  return (
    <Box sx={{ mt: 3, width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Related PDP Bundle List
        </Typography>
        <Chip label={bundles.length} size="small" color="primary" />
      </Box>
      <Grid container spacing={2}>
        {paged.map((item: any, idx: number) => (
          <Grid item xs={12} sm={6} md={4} key={idx}>
            <Card
              variant="outlined"
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                p: 1.5,
                gap: 2,
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: 3 },
              }}
            >
              {item.bundle_image || item.image ? (
                <Avatar
                  src={item.bundle_image || item.image}
                  variant="rounded"
                  sx={{ width: 72, height: 72, flexShrink: 0 }}
                  imgProps={{ style: { objectFit: "contain" } }}
                />
              ) : (
                <Avatar variant="rounded" sx={{ width: 72, height: 72, flexShrink: 0, bgcolor: "grey.200" }}>
                  <Typography variant="caption" color="text.secondary">
                    N/A
                  </Typography>
                </Avatar>
              )}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Tooltip title={item.bundle_name || item.name || ""} placement="top">
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      mb: 0.5,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {item.bundle_name || item.name || "Unnamed bundle"}
                  </Typography>
                </Tooltip>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={item.bundle_id}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.7rem", maxWidth: 180 }}
                  />
                  {item._fromGroup && (
                    <Chip label={item._fromGroup} size="small" color="info" sx={{ fontSize: "0.7rem" }} />
                  )}
                </Stack>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <MuiPagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} size="small" color="primary" />
        </Box>
      )}
    </Box>
  );
};

// Shows bundles for the current `pageTagId` (based on the form field)
const PageTagBundleList = () => {
  const dataProvider = useDataProvider();
  const pageTagId = useWatch({ name: "pageTagId" });
  const [bundles, setBundles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const selected = useMemo(() => {
    return typeof pageTagId === "string" ? pageTagId : "";
  }, [pageTagId]);

  useEffect(() => {
    if (!selected) {
      setBundles([]);
      return;
    }

    let cancelled = false;
    const fetchBundles = async () => {
      setLoading(true);
      try {
        const { data } = await dataProvider.getList("pdpGroup", {
          sort: { field: "pageTagId", order: "ASC" },
          pagination: { page: 1, perPage: 10000 },
          filter: {},
        });
        if (cancelled) return;
        const matched = data.filter((d: any) => d.pageTagId === selected);
        const allBundles = matched.flatMap((d: any) =>
          Array.isArray(d.bundleList) ? d.bundleList.map((b: any) => ({ ...b, _fromGroup: d.pageTagId })) : []
        );
        setBundles(allBundles);
      } catch (e) {
        console.error("Failed to fetch pageTag bundle list", e);
        if (!cancelled) setBundles([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchBundles();
    return () => {
      cancelled = true;
    };
  }, [selected, dataProvider]);

  useEffect(() => {
    setPage(1);
  }, [bundles.length]);

  if (loading) {
    return (
      <Box sx={{ mt: 3, width: "100%", display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading bundles for page tag...
        </Typography>
      </Box>
    );
  }
  if (bundles.length === 0) return null;

  const totalPages = Math.ceil(bundles.length / BUNDLES_PER_PAGE);
  const paged = bundles.slice((page - 1) * BUNDLES_PER_PAGE, page * BUNDLES_PER_PAGE);

  return (
    <Box sx={{ mt: 3, width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Page Tag Bundle List
        </Typography>
        <Chip label={bundles.length} size="small" color="primary" />
      </Box>
      <Grid container spacing={2}>
        {paged.map((item: any, idx: number) => (
          <Grid item xs={12} sm={6} md={4} key={idx}>
            <Card
              variant="outlined"
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                p: 1.5,
                gap: 2,
                transition: "box-shadow 0.2s",
                "&:hover": { boxShadow: 3 },
              }}
            >
              {item.bundle_image || item.image ? (
                <Avatar
                  src={item.bundle_image || item.image}
                  variant="rounded"
                  sx={{ width: 72, height: 72, flexShrink: 0 }}
                  imgProps={{ style: { objectFit: "contain" } }}
                />
              ) : (
                <Avatar variant="rounded" sx={{ width: 72, height: 72, flexShrink: 0, bgcolor: "grey.200" }}>
                  <Typography variant="caption" color="text.secondary">
                    N/A
                  </Typography>
                </Avatar>
              )}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Tooltip title={item.bundle_name || item.name || ""} placement="top">
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      mb: 0.5,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {item.bundle_name || item.name || "Unnamed bundle"}
                  </Typography>
                </Tooltip>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={item.bundle_id}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.7rem", maxWidth: 180 }}
                  />
                  {item._fromGroup && (
                    <Chip label={item._fromGroup} size="small" color="info" sx={{ fontSize: "0.7rem" }} />
                  )}
                </Stack>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <MuiPagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} size="small" color="primary" />
        </Box>
      )}
    </Box>
  );
};

const PdpGroupForm = ({ showRelated = true }: { showRelated?: boolean }) => (
  <>
    <TextInput source="pageTagId" label="Page Tag Id" fullWidth />
    <TextInput source="url" label="URL" fullWidth />
    <SelectInput
      source="country"
      label="Country"
      fullWidth
      choices={[
        { id: "GB", name: "GB" },
        { id: "DE", name: "DE" },
        { id: "IT", name: "IT" },
        { id: "FR", name: "FR" },
      ]}
    />
    {showRelated && <RelatedPdpCrossSellInput source="relatedPdpCrossSell" label="Related PDP Cross Sell" />}
    <FilterListInput source="filterList" label="Filter List" />
    <Box sx={{ mb: 2, width: "100%" }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
        Filter Value
      </Typography>
      <JsonField source="filterValue" label="" />
    </Box>
  </>
);

// EDIT
export const PdpGroupEdit = () => (
  <Edit mutationMode="pessimistic">
    <SimpleForm
      toolbar={
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <SaveButton label="Update" />
          <DeleteButton
            label="Delete"
            sx={{ textAlign: "right" }}
            mutationMode="pessimistic"
            confirmTitle="Delete PDP Group"
          />
        </Toolbar>
      }
    >
      <TextInput source="pdpGroupId" label="PDP Group ID" fullWidth disabled />
      <PdpGroupForm showRelated={false} />
      <RelatedPdpCrossSellInput source="relatedPdpCrossSell" label="Related PDP Cross Sell" />
      <DynamicBundleList />
      <PageTagBundleList />
    </SimpleForm>
  </Edit>
);

// CREATE
export const PdpGroupCreate = () => (
  <Create>
    <SimpleForm>
      <PdpGroupForm />
    </SimpleForm>
  </Create>
);
